import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MapPin,
  Users,
  Search,
  Filter,
  ArrowUpDown,
  ChevronRight,
  ArrowLeft,
  Calendar,
  Building2,
  ExternalLink,
  RefreshCw,
  Globe,
  UserCheck,
  User,
  Phone,
  Mail,
  ChevronLeft,
  Sparkles,
  Layers,
  CheckCircle2,
  X,
} from "lucide-react";
import {
  GetLocationAnalyticsOverview,
  GetLocationStateAnalytics,
  GetLocationCityAnalytics,
  GetLocationUsers,
} from "./apis/get";

// Types
interface OverviewStats {
  total_profiles: number;
  total_states: number;
  total_cities: number;
  total_males: number;
  total_females: number;
  total_others: number;
  male_percentage: number;
  female_percentage: number;
  top_state: { state: string; total: number } | null;
  top_city: { city: string; state: string; total: number } | null;
}

interface StateStat {
  state: string;
  total: number;
  males: number;
  females: number;
  others: number;
  male_percentage: number;
  female_percentage: number;
  distinct_cities: number;
  percentage_of_total: number;
}

interface CityStat {
  city: string;
  state: string;
  total: number;
  males: number;
  females: number;
  others: number;
  male_percentage: number;
  female_percentage: number;
  percentage_of_total: number;
}

interface UserRecord {
  user_id: string;
  matrimonial_id: string | null;
  name: string;
  username: string | null;
  gender: string;
  state: string;
  city: string;
  stored_state: string | null;
  marital_status: string;
  phone_number: string | null;
  email_address: string | null;
  user_type: string;
  progress: number;
  is_active: boolean | null;
  occupation: string | null;
  annual_income_inr: number | null;
  created_at: string | null;
}

const SEARCH_DEBOUNCE_MS = 350;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Start/end of a local calendar day as UTC ISO strings (backend converts to naive UTC)
const localDayStartIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};
const localDayEndIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

export const StateCityAnalytics: React.FC = () => {
  const navigate = useNavigate();
  // Latest request id per list; responses from older requests are dropped
  const requestIds = useRef({ overview: 0, states: 0, cities: 0, users: 0, cityOptions: 0 });

  // Overview KPIs
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [loadingOverview, setLoadingOverview] = useState<boolean>(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  // Filters
  const [datePreset, setDatePreset] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [userType, setUserType] = useState<string>("all");
  const [genderFilter, setGenderFilter] = useState<string>("all");

  // State Level State
  const [states, setStates] = useState<StateStat[]>([]);
  const [stateSearch, setStateSearch] = useState<string>("");
  const [stateSortBy, setStateSortBy] = useState<string>("total");
  const [stateSortOrder, setStateSortOrder] = useState<string>("desc");
  const [statePage, setStatePage] = useState<number>(0);
  const [totalStatesCount, setTotalStatesCount] = useState<number>(0);
  const [loadingStates, setLoadingStates] = useState<boolean>(false);
  const STATE_PAGE_SIZE = 20;

  // Selected State Drilldown (City Level)
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [cities, setCities] = useState<CityStat[]>([]);
  const [citySearch, setCitySearch] = useState<string>("");
  const [citySortBy, setCitySortBy] = useState<string>("total");
  const [citySortOrder, setCitySortOrder] = useState<string>("desc");
  const [cityPage, setCityPage] = useState<number>(0);
  const [totalCitiesCount, setTotalCitiesCount] = useState<number>(0);
  const [citySummary, setCitySummary] = useState<{ total_profiles: number; total_males: number; total_females: number } | null>(null);
  const [loadingCities, setLoadingCities] = useState<boolean>(false);
  const CITY_PAGE_SIZE = 20;

  // Selected City & User Drilldown
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"users" | "breakdown">("users");

  // Dropdown options
  const [stateOptions, setStateOptions] = useState<{ name: string; total: number }[]>([]);
  const [cityOptions, setCityOptions] = useState<{ name: string; total: number }[]>([]);
  const [loadingCityOptions, setLoadingCityOptions] = useState<boolean>(false);

  // Users Directory State
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [userSearch, setUserSearch] = useState<string>("");
  const [userSortBy, setUserSortBy] = useState<string>("created_at");
  const [userSortOrder, setUserSortOrder] = useState<string>("desc");
  const [userPage, setUserPage] = useState<number>(0);
  const [totalUsersCount, setTotalUsersCount] = useState<number>(0);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const USER_PAGE_SIZE = 20;

  const debouncedStateSearch = useDebouncedValue(stateSearch, SEARCH_DEBOUNCE_MS);
  const debouncedCitySearch = useDebouncedValue(citySearch, SEARCH_DEBOUNCE_MS);
  const debouncedUserSearch = useDebouncedValue(userSearch, SEARCH_DEBOUNCE_MS);

  // Calculate Date Ranges
  const getDateRangeParams = useCallback(() => {
    const params: { from_date?: string; to_date?: string } = {};

    if (datePreset === "custom") {
      if (fromDate) params.from_date = localDayStartIso(new Date(`${fromDate}T00:00:00`));
      if (toDate) params.to_date = localDayEndIso(new Date(`${toDate}T00:00:00`));
      return params;
    }

    if (datePreset === "today") {
      const now = new Date();
      params.from_date = localDayStartIso(now);
      params.to_date = localDayEndIso(now);
    } else if (datePreset === "7d") {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      params.from_date = d.toISOString();
    } else if (datePreset === "30d") {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      params.from_date = d.toISOString();
    } else if (datePreset === "90d") {
      const d = new Date();
      d.setDate(d.getDate() - 90);
      params.from_date = d.toISOString();
    }

    return params;
  }, [datePreset, fromDate, toDate]);

  // Fetch Overview KPIs (overall totals; loaded on open and on Refresh only)
  const fetchOverview = useCallback(async () => {
    const reqId = ++requestIds.current.overview;
    try {
      setLoadingOverview(true);
      setOverviewError(null);
      const res = await GetLocationAnalyticsOverview();
      if (reqId !== requestIds.current.overview) return;
      if (res && res.status === "success" && res.data) {
        setOverview(res.data);
      } else {
        setOverviewError("Unexpected response from overview API");
      }
    } catch (err: any) {
      if (reqId !== requestIds.current.overview) return;
      console.error("Error fetching location overview:", err);
      setOverview(null);
      setOverviewError(err?.message || "Failed to load overview");
    } finally {
      if (reqId === requestIds.current.overview) setLoadingOverview(false);
    }
  }, []);

  // Fetch States
  const fetchStates = useCallback(async () => {
    const reqId = ++requestIds.current.states;
    try {
      setLoadingStates(true);
      const dateParams = getDateRangeParams();
      const params: any = {
        limit: STATE_PAGE_SIZE,
        offset: statePage * STATE_PAGE_SIZE,
        sort_by: stateSortBy,
        sort_order: stateSortOrder,
        ...dateParams,
      };

      if (debouncedStateSearch.trim()) params.search = debouncedStateSearch.trim();
      if (genderFilter !== "all") params.gender = genderFilter;
      if (userType !== "all") params.user_type = userType;

      const res = await GetLocationStateAnalytics(params);
      if (reqId !== requestIds.current.states) return;
      if (res && res.status === "success" && res.data) {
        setStates(res.data.states || []);
        setTotalStatesCount(res.data.total_states || 0);
      }
    } catch (err) {
      console.error("Error fetching state analytics:", err);
    } finally {
      if (reqId === requestIds.current.states) setLoadingStates(false);
    }
  }, [statePage, stateSortBy, stateSortOrder, debouncedStateSearch, genderFilter, userType, getDateRangeParams]);

  // Fetch Cities (for selected state)
  const fetchCities = useCallback(async () => {
    if (!selectedState) return;
    const reqId = ++requestIds.current.cities;
    try {
      setLoadingCities(true);
      const dateParams = getDateRangeParams();
      const params: any = {
        state: selectedState,
        limit: CITY_PAGE_SIZE,
        offset: cityPage * CITY_PAGE_SIZE,
        sort_by: citySortBy,
        sort_order: citySortOrder,
        ...dateParams,
      };

      if (debouncedCitySearch.trim()) params.search = debouncedCitySearch.trim();
      if (genderFilter !== "all") params.gender = genderFilter;
      if (userType !== "all") params.user_type = userType;

      const res = await GetLocationCityAnalytics(params);
      if (reqId !== requestIds.current.cities) return;
      if (res && res.status === "success" && res.data) {
        setCities(res.data.cities || []);
        setTotalCitiesCount(res.data.total_cities || 0);
        setCitySummary(res.data.summary || null);
      }
    } catch (err) {
      console.error("Error fetching city analytics:", err);
    } finally {
      if (reqId === requestIds.current.cities) setLoadingCities(false);
    }
  }, [selectedState, cityPage, citySortBy, citySortOrder, debouncedCitySearch, genderFilter, userType, getDateRangeParams]);

  // Fetch Users (for directory / drilldown)
  const fetchUsers = useCallback(async () => {
    const reqId = ++requestIds.current.users;
    try {
      setLoadingUsers(true);
      const dateParams = getDateRangeParams();
      const params: any = {
        limit: USER_PAGE_SIZE,
        offset: userPage * USER_PAGE_SIZE,
        sort_by: userSortBy,
        sort_order: userSortOrder,
        ...dateParams,
      };

      if (selectedState) params.state = selectedState;
      if (selectedCity) params.city = selectedCity;
      if (debouncedUserSearch.trim()) params.search = debouncedUserSearch.trim();
      if (genderFilter !== "all") params.gender = genderFilter;
      if (userType !== "all") params.user_type = userType;

      const res = await GetLocationUsers(params);
      if (reqId !== requestIds.current.users) return;
      if (res && res.status === "success" && res.data) {
        setUsers(res.data.users || []);
        setTotalUsersCount(res.data.total_users || 0);
      }
    } catch (err) {
      console.error("Error fetching location users:", err);
    } finally {
      if (reqId === requestIds.current.users) setLoadingUsers(false);
    }
  }, [selectedState, selectedCity, userPage, userSortBy, userSortOrder, debouncedUserSearch, genderFilter, userType, getDateRangeParams]);

  // Same state/city can be stored with different casing; merge them for the dropdowns
  const mergeOptions = (rows: { name: string; total: number }[]) => {
    const merged = new Map<string, { name: string; total: number }>();
    rows.forEach((r) => {
      const key = r.name.trim().toLowerCase();
      const existing = merged.get(key);
      if (existing) existing.total += r.total;
      else merged.set(key, { name: r.name.trim(), total: r.total });
    });
    // Non-state buckets go to the end of the list
    const rank = (n: string) => (n === "Unknown" ? 2 : n === "Outside India" ? 1 : 0);
    return Array.from(merged.values()).sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
  };

  const fetchStateOptions = useCallback(async () => {
    try {
      const res = await GetLocationStateAnalytics({ limit: 500, sort_by: "state", sort_order: "asc" });
      if (res && res.status === "success" && res.data) {
        setStateOptions(mergeOptions((res.data.states || []).map((s: StateStat) => ({ name: s.state, total: s.total }))));
      }
    } catch (err) {
      console.error("Error fetching state options:", err);
    }
  }, []);

  const fetchCityOptions = useCallback(async (stateName: string) => {
    const reqId = ++requestIds.current.cityOptions;
    try {
      setLoadingCityOptions(true);
      setCityOptions([]);
      const res = await GetLocationCityAnalytics({ state: stateName, limit: 500, sort_by: "city", sort_order: "asc" });
      if (reqId !== requestIds.current.cityOptions) return;
      if (res && res.status === "success" && res.data) {
        setCityOptions(mergeOptions((res.data.cities || []).map((c: CityStat) => ({ name: c.city, total: c.total }))));
      }
    } catch (err) {
      console.error("Error fetching city options:", err);
    } finally {
      if (reqId === requestIds.current.cityOptions) setLoadingCityOptions(false);
    }
  }, []);

  // Initial Load
  useEffect(() => {
    fetchOverview();
    fetchStateOptions();
  }, [fetchOverview, fetchStateOptions]);

  useEffect(() => {
    if (selectedState) {
      fetchCityOptions(selectedState);
    } else {
      requestIds.current.cityOptions++;
      setCityOptions([]);
      setLoadingCityOptions(false);
    }
  }, [selectedState, fetchCityOptions]);

  useEffect(() => {
    if (activeTab === "breakdown" && !selectedState) {
      fetchStates();
    }
  }, [fetchStates, activeTab, selectedState]);

  useEffect(() => {
    if (activeTab === "breakdown" && selectedState) {
      fetchCities();
    }
  }, [fetchCities, activeTab, selectedState]);

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    }
  }, [fetchUsers, activeTab]);

  const resetPages = () => {
    setStatePage(0);
    setCityPage(0);
    setUserPage(0);
  };

  // Filter bar: state dropdown
  const handleStateFilterChange = (value: string) => {
    setSelectedState(value || null);
    setSelectedCity(null);
    setCityPage(0);
    setCitySearch("");
    setUserPage(0);
  };

  // Filter bar: city dropdown
  const handleCityFilterChange = (value: string) => {
    setSelectedCity(value || null);
    setUserPage(0);
  };

  const handleResetFilters = () => {
    setSelectedState(null);
    setSelectedCity(null);
    setGenderFilter("all");
    setUserType("all");
    setDatePreset("all");
    setFromDate("");
    setToDate("");
    setUserSearch("");
    setStatePage(0);
    setCityPage(0);
    setUserPage(0);
  };

  // Drilldown action: Click State
  const handleSelectState = (stateName: string) => {
    setSelectedState(stateName);
    setSelectedCity(null);
    setCityPage(0);
    setCitySearch("");
    setActiveTab("breakdown");
  };

  // Drilldown action: View Users from State
  const handleViewUsersFromState = (stateName: string) => {
    setSelectedState(stateName);
    setSelectedCity(null);
    setUserPage(0);
    setUserSearch("");
    setActiveTab("users");
  };

  // Drilldown action: View Users from City
  const handleViewUsersFromCity = (cityName: string, stateName: string) => {
    setSelectedState(stateName);
    setSelectedCity(cityName);
    setUserPage(0);
    setUserSearch("");
    setActiveTab("users");
  };

  // Reset drilldown to states
  const handleBackToStates = () => {
    setSelectedState(null);
    setSelectedCity(null);
    setActiveTab("breakdown");
  };

  const handleRefreshAll = () => {
    fetchOverview();
    fetchStateOptions();
    if (selectedState) fetchCityOptions(selectedState);
    if (activeTab === "breakdown") {
      if (selectedState) fetchCities();
      else fetchStates();
    }
    if (activeTab === "users") fetchUsers();
  };

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 p-4 sm:p-6 lg:p-8">
      {/* Header Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800/80">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-tr from-sky-600 to-indigo-600 text-white shadow-lg shadow-sky-500/20">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-2">
                State & City Analytics
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20 uppercase tracking-wider">
                  Live DB
                </span>
              </h1>
              <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                Geographic user distribution, demographic breakdown & candidate directory
              </p>
            </div>
          </div>
        </div>

        {/* Global Action / Refresh */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefreshAll}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-800/80 hover:bg-slate-700/80 text-slate-300 hover:text-white border border-slate-700/80 text-xs font-semibold transition shadow-sm"
            title="Refresh All Data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingStates || loadingCities || loadingUsers ? "animate-spin text-sky-400" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {overviewError && (
        <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-xs text-rose-300">
          <span>Overview stats could not be loaded: {overviewError}</span>
          <button onClick={fetchOverview} className="font-semibold text-rose-200 hover:underline">
            Retry
          </button>
        </div>
      )}

      {/* Overview Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-6">
        {/* Card 1: Total Profiles */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-800/60 to-slate-900/60 p-5 border border-slate-800 shadow-xl backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Profiles</span>
            <div className="p-2 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-black text-white tracking-tight">
              {loadingOverview ? "..." : (overview?.total_profiles || 0).toLocaleString()}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
              <span className="text-emerald-400 font-bold flex items-center gap-1">
                <Globe className="w-3.5 h-3.5" />
                {overview?.total_states || 0} States
              </span>
              <span>•</span>
              <span className="text-indigo-400 font-bold">{overview?.total_cities || 0} Cities</span>
            </div>
          </div>
        </div>

        {/* Card 2: Male Users */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-800/60 to-slate-900/60 p-5 border border-slate-800 shadow-xl backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Male Candidates</span>
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <User className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-black text-blue-400 tracking-tight">
              {loadingOverview ? "..." : (overview?.total_males || 0).toLocaleString()}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-slate-400 font-medium">Share of Total</span>
              <span className="text-blue-300 font-bold font-mono">{overview?.male_percentage || 0}%</span>
            </div>
            <div className="w-full bg-slate-800 h-1.5 rounded-full mt-1.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-600 to-blue-400 h-full rounded-full transition-all duration-500"
                style={{ width: `${overview?.male_percentage || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Card 3: Female Users */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-800/60 to-slate-900/60 p-5 border border-slate-800 shadow-xl backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Female Candidates</span>
            <div className="p-2 rounded-xl bg-rose-500/10 text-rose-400 border border-rose-500/20">
              <UserCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-3xl font-black text-rose-400 tracking-tight">
              {loadingOverview ? "..." : (overview?.total_females || 0).toLocaleString()}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-slate-400 font-medium">Share of Total</span>
              <span className="text-rose-300 font-bold font-mono">{overview?.female_percentage || 0}%</span>
            </div>
            <div className="w-full bg-slate-800 h-1.5 rounded-full mt-1.5 overflow-hidden">
              <div
                className="bg-gradient-to-r from-rose-600 to-rose-400 h-full rounded-full transition-all duration-500"
                style={{ width: `${overview?.female_percentage || 0}%` }}
              />
            </div>
          </div>
        </div>

        {/* Card 4: Top Territory */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-slate-800/60 to-slate-900/60 p-5 border border-slate-800 shadow-xl backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Top State & City</span>
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Leading State</div>
              <div className="text-lg font-black text-white flex items-center justify-between">
                <span className="truncate">{overview?.top_state?.state || "N/A"}</span>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {overview?.top_state?.total || 0} users
                </span>
              </div>
            </div>
            <div className="pt-1 border-t border-slate-800/80">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Leading City</div>
              <div className="text-sm font-bold text-slate-200 flex items-center justify-between">
                <span className="truncate">{overview?.top_city?.city || "N/A"}</span>
                <span className="text-xs font-bold text-indigo-400">
                  {overview?.top_city?.total || 0} users
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Filter Bar */}
      <div className="rounded-2xl bg-slate-900/70 border border-slate-800 p-4 mb-6 shadow-md backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Filter className="w-3.5 h-3.5 text-sky-400" />
            <span>Filters:</span>
          </div>

          {/* Date Preset Selector */}
          <div className="flex items-center gap-1 bg-slate-800/80 p-1 rounded-xl border border-slate-700/60">
            {[
              { id: "all", label: "All Time" },
              { id: "today", label: "Today" },
              { id: "7d", label: "7 Days" },
              { id: "30d", label: "30 Days" },
              { id: "custom", label: "Custom" },
            ].map((preset) => (
              <button
                key={preset.id}
                onClick={() => {
                  setDatePreset(preset.id);
                  resetPages();
                }}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${
                  datePreset === preset.id
                    ? "bg-sky-500 text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom Date Pickers */}
          {datePreset === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  resetPages();
                }}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-sky-500"
              />
              <span className="text-slate-500 text-xs">to</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  resetPages();
                }}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-sky-500"
              />
            </div>
          )}

          {/* User Type Filter */}
          <select
            value={userType}
            onChange={(e) => {
              setUserType(e.target.value);
              setStatePage(0);
              setCityPage(0);
              setUserPage(0);
            }}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-sky-500 font-medium"
          >
            <option value="all">All User Types</option>
            <option value="registered">Registered</option>
            <option value="guest">Guest</option>
            <option value="mediator">Mediator</option>
          </select>

          {/* Gender Filter */}
          <select
            value={genderFilter}
            onChange={(e) => {
              setGenderFilter(e.target.value);
              setStatePage(0);
              setCityPage(0);
              setUserPage(0);
            }}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-sky-500 font-medium"
          >
            <option value="all">All Genders</option>
            <option value="male">Male Only</option>
            <option value="female">Female Only</option>
          </select>

          {/* State Filter */}
          <select
            value={selectedState || ""}
            onChange={(e) => handleStateFilterChange(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-sky-500 font-medium max-w-[200px]"
          >
            <option value="">All States</option>
            {stateOptions.map((s) => (
              <option key={s.name} value={s.name}>
                {s.name} ({s.total})
              </option>
            ))}
          </select>

          {/* City Filter (cities of the selected state) */}
          <select
            value={selectedCity || ""}
            onChange={(e) => handleCityFilterChange(e.target.value)}
            disabled={!selectedState || loadingCityOptions}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-3 py-1.5 focus:outline-none focus:border-sky-500 font-medium max-w-[200px] disabled:opacity-50 disabled:cursor-not-allowed"
            title={selectedState ? "" : "Select a state first"}
          >
            <option value="">
              {!selectedState ? "Select state first" : loadingCityOptions ? "Loading cities..." : "All Cities"}
            </option>
            {cityOptions.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name} ({c.total})
              </option>
            ))}
          </select>

          {(selectedState || selectedCity || genderFilter !== "all" || userType !== "all" || datePreset !== "all") && (
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-semibold text-rose-400 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 transition"
            >
              <X className="w-3.5 h-3.5" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Navigation Breadcrumb & View Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        {/* Breadcrumb Trail */}
        <div className="flex items-center gap-2 text-sm font-semibold">
          <button
            onClick={() => handleStateFilterChange("")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition ${
              !selectedState
                ? "bg-sky-500/10 text-sky-400 border-sky-500/30"
                : "bg-slate-900/60 text-slate-400 border-slate-800 hover:text-white"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>All States ({stateOptions.filter((s) => s.name !== "Unknown" && s.name !== "Outside India").length})</span>
          </button>

          {selectedState && (
            <>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <button
                onClick={() => {
                  setSelectedCity(null);
                  setActiveTab("breakdown");
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border transition ${
                  selectedState && !selectedCity && activeTab === "breakdown"
                    ? "bg-sky-500/10 text-sky-400 border-sky-500/30"
                    : "bg-slate-900/60 text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                <MapPin className="w-3.5 h-3.5 text-sky-400" />
                <span>{selectedState}</span>
              </button>
            </>
          )}

          {selectedCity && (
            <>
              <ChevronRight className="w-4 h-4 text-slate-600" />
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-500/10 text-indigo-300 border border-indigo-500/30">
                <Building2 className="w-3.5 h-3.5 text-indigo-400" />
                <span>{selectedCity}</span>
              </div>
            </>
          )}
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 bg-slate-900/80 p-1 rounded-xl border border-slate-800">
          <button
            onClick={() => setActiveTab("users")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              activeTab === "users"
                ? "bg-slate-800 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Users ({totalUsersCount})
          </button>

          <button
            onClick={() => setActiveTab("breakdown")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              activeTab === "breakdown"
                ? "bg-slate-800 text-white shadow-sm"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            {selectedState ? `City-wise (${selectedState})` : "State-wise"}
          </button>
        </div>
      </div>

      {/* VIEW 1: STATES TABLE & ANALYTICS */}
      {activeTab === "breakdown" && !selectedState && (
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 shadow-xl overflow-hidden backdrop-blur-sm">
          {/* Table Search & Controls */}
          <div className="p-4 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search state name..."
                value={stateSearch}
                onChange={(e) => {
                  setStateSearch(e.target.value);
                  setStatePage(0);
                }}
                className="w-full bg-slate-800/80 border border-slate-700/80 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-sky-500 placeholder-slate-500"
              />
              {stateSearch && (
                <button
                  onClick={() => setStateSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 font-medium">Sort by:</span>
              <select
                value={stateSortBy}
                onChange={(e) => {
                  setStateSortBy(e.target.value);
                  setStatePage(0);
                }}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-sky-500"
              >
                <option value="total">Most Users</option>
                <option value="state">State Name</option>
                <option value="males">Most Males</option>
                <option value="females">Most Females</option>
                <option value="distinct_cities">Most Cities</option>
              </select>

              <button
                onClick={() => {
                  setStateSortOrder(stateSortOrder === "asc" ? "desc" : "asc");
                  setStatePage(0);
                }}
                className="p-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 hover:text-white"
                title="Toggle sort direction"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-950/40">
                  <th className="py-3 px-4">State Name</th>
                  <th className="py-3 px-4">Total Users</th>
                  <th className="py-3 px-4">Male Users</th>
                  <th className="py-3 px-4">Female Users</th>
                  <th className="py-3 px-4">Gender Ratio Bar</th>
                  <th className="py-3 px-4">Active Cities</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-xs font-medium">
                {loadingStates ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-sky-400" />
                      Loading state analytics...
                    </td>
                  </tr>
                ) : states.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500">
                      No states found matching current filters.
                    </td>
                  </tr>
                ) : (
                  states.map((st, idx) => (
                    <tr
                      key={st.state || idx}
                      className="hover:bg-slate-800/30 transition-colors group"
                    >
                      {/* State */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-sky-500/10 text-sky-400 border border-sky-500/20">
                            <MapPin className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <span className="font-bold text-white text-sm group-hover:text-sky-300 transition">
                              {st.state}
                            </span>
                            <div className="text-[10px] text-slate-500 font-mono">
                              {st.percentage_of_total}% of all profiles
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Total */}
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-200">
                        <span className="px-2.5 py-1 rounded-full bg-slate-800 text-white border border-slate-700">
                          {st.total.toLocaleString()}
                        </span>
                      </td>

                      {/* Males */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-blue-400 font-mono">
                            {st.males.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {st.male_percentage}%
                          </span>
                        </div>
                      </td>

                      {/* Females */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-rose-400 font-mono">
                            {st.females.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {st.female_percentage}%
                          </span>
                        </div>
                      </td>

                      {/* Gender Bar */}
                      <td className="py-3.5 px-4 min-w-[140px]">
                        <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden flex">
                          <div
                            className="bg-blue-500 h-full"
                            style={{ width: `${st.male_percentage}%` }}
                            title={`Male: ${st.males} (${st.male_percentage}%)`}
                          />
                          <div
                            className="bg-rose-500 h-full"
                            style={{ width: `${st.female_percentage}%` }}
                            title={`Female: ${st.females} (${st.female_percentage}%)`}
                          />
                        </div>
                      </td>

                      {/* Distinct Cities */}
                      <td className="py-3.5 px-4 font-mono text-slate-300">
                        <span className="flex items-center gap-1.5 text-xs">
                          <Building2 className="w-3.5 h-3.5 text-indigo-400" />
                          {st.distinct_cities} cities
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleSelectState(st.state)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 border border-sky-500/20 text-xs font-semibold transition"
                          >
                            <span>View Cities</span>
                            <ChevronRight className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleViewUsersFromState(st.state)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-xs font-semibold transition"
                          >
                            <span>Users</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div>
              Showing {states.length > 0 ? statePage * STATE_PAGE_SIZE + 1 : 0} to{" "}
              {Math.min((statePage + 1) * STATE_PAGE_SIZE, totalStatesCount)} of {totalStatesCount} states
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={statePage === 0 || loadingStates}
                onClick={() => setStatePage((p) => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Previous
              </button>
              <span className="font-mono font-bold text-slate-300">
                Page {statePage + 1} of {Math.max(1, Math.ceil(totalStatesCount / STATE_PAGE_SIZE))}
              </span>
              <button
                disabled={(statePage + 1) * STATE_PAGE_SIZE >= totalStatesCount || loadingStates}
                onClick={() => setStatePage((p) => p + 1)}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 2: CITIES TABLE (SELECTED STATE) */}
      {activeTab === "breakdown" && selectedState && (
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 shadow-xl overflow-hidden backdrop-blur-sm">
          {/* Banner for State */}
          <div className="p-4 bg-gradient-to-r from-sky-950/40 via-indigo-950/30 to-slate-900 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToStates}
                className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition"
                title="Back to All States"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-black text-white">{selectedState}</h2>
                  <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20">
                    {totalCitiesCount} Active Cities
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  Total in state: {citySummary?.total_profiles || 0} users ({citySummary?.total_males || 0} Males, {citySummary?.total_females || 0} Females)
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleViewUsersFromState(selectedState)}
                className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-sky-600 to-indigo-600 text-white text-xs font-bold shadow-md shadow-sky-500/20 hover:brightness-110 transition flex items-center gap-1.5"
              >
                <Users className="w-3.5 h-3.5" />
                <span>View All {selectedState} Users</span>
              </button>
            </div>
          </div>

          {/* Search & Sort Controls */}
          <div className="p-4 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={`Search city in ${selectedState}...`}
                value={citySearch}
                onChange={(e) => {
                  setCitySearch(e.target.value);
                  setCityPage(0);
                }}
                className="w-full bg-slate-800/80 border border-slate-700/80 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-sky-500 placeholder-slate-500"
              />
              {citySearch && (
                <button
                  onClick={() => setCitySearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 font-medium">Sort by:</span>
              <select
                value={citySortBy}
                onChange={(e) => {
                  setCitySortBy(e.target.value);
                  setCityPage(0);
                }}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-sky-500"
              >
                <option value="total">Most Users</option>
                <option value="city">City Name</option>
                <option value="males">Most Males</option>
                <option value="females">Most Females</option>
              </select>

              <button
                onClick={() => {
                  setCitySortOrder(citySortOrder === "asc" ? "desc" : "asc");
                  setCityPage(0);
                }}
                className="p-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 hover:text-white"
                title="Toggle sort direction"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-950/40">
                  <th className="py-3 px-4">City Name</th>
                  <th className="py-3 px-4">Total Users</th>
                  <th className="py-3 px-4">Male Users</th>
                  <th className="py-3 px-4">Female Users</th>
                  <th className="py-3 px-4">Gender Ratio Bar</th>
                  <th className="py-3 px-4">% of State</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-xs font-medium">
                {loadingCities ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-sky-400" />
                      Loading cities for {selectedState}...
                    </td>
                  </tr>
                ) : cities.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-500">
                      No cities found in {selectedState} matching criteria.
                    </td>
                  </tr>
                ) : (
                  cities.map((ct, idx) => (
                    <tr
                      key={ct.city || idx}
                      className="hover:bg-slate-800/30 transition-colors group"
                    >
                      {/* City */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                            <Building2 className="w-3.5 h-3.5" />
                          </div>
                          <div>
                            <span className="font-bold text-white text-sm group-hover:text-indigo-300 transition">
                              {ct.city}
                            </span>
                            <div className="text-[10px] text-slate-500">{ct.state}</div>
                          </div>
                        </div>
                      </td>

                      {/* Total */}
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-200">
                        <span className="px-2.5 py-1 rounded-full bg-slate-800 text-white border border-slate-700">
                          {ct.total.toLocaleString()}
                        </span>
                      </td>

                      {/* Males */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-blue-400 font-mono">
                            {ct.males.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {ct.male_percentage}%
                          </span>
                        </div>
                      </td>

                      {/* Females */}
                      <td className="py-3.5 px-4">
                        <div className="flex flex-col">
                          <span className="font-bold text-rose-400 font-mono">
                            {ct.females.toLocaleString()}
                          </span>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {ct.female_percentage}%
                          </span>
                        </div>
                      </td>

                      {/* Gender Bar */}
                      <td className="py-3.5 px-4 min-w-[140px]">
                        <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden flex">
                          <div
                            className="bg-blue-500 h-full"
                            style={{ width: `${ct.male_percentage}%` }}
                            title={`Male: ${ct.males} (${ct.male_percentage}%)`}
                          />
                          <div
                            className="bg-rose-500 h-full"
                            style={{ width: `${ct.female_percentage}%` }}
                            title={`Female: ${ct.females} (${ct.female_percentage}%)`}
                          />
                        </div>
                      </td>

                      {/* % of State */}
                      <td className="py-3.5 px-4 font-mono text-slate-300">
                        {ct.percentage_of_total}%
                      </td>

                      {/* Action */}
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => handleViewUsersFromCity(ct.city, ct.state)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 border border-indigo-500/20 text-xs font-semibold transition"
                        >
                          <Users className="w-3 h-3" />
                          <span>View Users</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div>
              Showing {cities.length > 0 ? cityPage * CITY_PAGE_SIZE + 1 : 0} to{" "}
              {Math.min((cityPage + 1) * CITY_PAGE_SIZE, totalCitiesCount)} of {totalCitiesCount} cities
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={cityPage === 0 || loadingCities}
                onClick={() => setCityPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Previous
              </button>
              <span className="font-mono font-bold text-slate-300">
                Page {cityPage + 1} of {Math.max(1, Math.ceil(totalCitiesCount / CITY_PAGE_SIZE))}
              </span>
              <button
                disabled={(cityPage + 1) * CITY_PAGE_SIZE >= totalCitiesCount || loadingCities}
                onClick={() => setCityPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW 3: USERS DIRECTORY (WITH REDIRECT TO PROFILE) */}
      {activeTab === "users" && (
        <div className="rounded-2xl bg-slate-900/70 border border-slate-800 shadow-xl overflow-hidden backdrop-blur-sm">
          {/* Header & Location scope */}
          <div className="p-4 bg-slate-950/40 border-b border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-black text-white">Candidates Directory</h2>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                  {totalUsersCount} Total Users
                </span>
              </div>
              <div className="text-xs text-slate-400 flex items-center gap-2 mt-1">
                <span>Scope:</span>
                {selectedState ? (
                  <span className="font-bold text-sky-400">
                    State: {selectedState} {selectedCity ? `> City: ${selectedCity}` : ""}
                  </span>
                ) : (
                  <span className="text-slate-500 italic">All States & Cities</span>
                )}
                {(selectedState || selectedCity) && (
                  <button
                    onClick={() => handleStateFilterChange("")}
                    className="text-[11px] text-rose-400 hover:underline ml-2"
                  >
                    Clear location scope
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search & Sort Controls */}
          <div className="p-4 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search user name, ID, phone, email..."
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserPage(0);
                }}
                className="w-full bg-slate-800/80 border border-slate-700/80 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-sky-500 placeholder-slate-500"
              />
              {userSearch && (
                <button
                  onClick={() => setUserSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 font-medium">Sort by:</span>
              <select
                value={userSortBy}
                onChange={(e) => {
                  setUserSortBy(e.target.value);
                  setUserPage(0);
                }}
                className="bg-slate-800 border border-slate-700 text-slate-200 text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:border-sky-500"
              >
                <option value="created_at">Registration Date</option>
                <option value="name">Candidate Name</option>
                <option value="user_id">User ID</option>
                <option value="progress">Profile Progress</option>
              </select>

              <button
                onClick={() => {
                  setUserSortOrder(userSortOrder === "asc" ? "desc" : "asc");
                  setUserPage(0);
                }}
                className="p-2 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 hover:text-white"
                title="Toggle sort direction"
              >
                <ArrowUpDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-800 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-950/40">
                  <th className="py-3 px-4">Candidate Profile</th>
                  <th className="py-3 px-4">Contact Info</th>
                  <th className="py-3 px-4">Gender & Status</th>
                  <th className="py-3 px-4">Location (City, State)</th>
                  <th className="py-3 px-4">Type & Progress</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-xs font-medium">
                {loadingUsers ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-sky-400" />
                      Loading candidates...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-500">
                      No candidates found matching the criteria.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr
                      key={u.user_id}
                      className="hover:bg-slate-800/30 transition-colors group cursor-pointer"
                      onClick={() => navigate(`/users/user/${encodeURIComponent(u.user_id)}`)}
                    >
                      {/* Name & ID */}
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex flex-col">
                          <Link
                            to={`/users/user/${encodeURIComponent(u.user_id)}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-bold text-white text-sm hover:text-sky-400 transition flex items-center gap-1.5"
                          >
                            <span>{u.name}</span>
                            <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 text-sky-400 transition" />
                          </Link>
                          <span className="text-[10px] font-mono text-slate-500 mt-0.5">
                            {u.user_id}
                          </span>
                          {u.matrimonial_id && (
                            <span className="text-[10px] text-slate-600 font-mono">
                              MID: {u.matrimonial_id}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Contact Info */}
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex flex-col gap-1 text-slate-300">
                          {u.phone_number ? (
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3 h-3 text-slate-500" />
                              <span className="font-mono">{u.phone_number}</span>
                            </div>
                          ) : (
                            <span className="text-slate-600 text-[11px] italic">No Phone</span>
                          )}
                          {u.email_address ? (
                            <div className="flex items-center gap-1.5 text-slate-400 text-[11px] truncate max-w-[180px]">
                              <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                              <span className="truncate">{u.email_address}</span>
                            </div>
                          ) : null}
                        </div>
                      </td>

                      {/* Gender & Marital Status */}
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight border ${
                              u.gender.toLowerCase() === "male"
                                ? "bg-blue-500/10 text-blue-400 border-blue-500/20"
                                : u.gender.toLowerCase() === "female"
                                ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
                                : "bg-slate-800 text-slate-400 border-slate-700"
                            }`}
                          >
                            {u.gender || "N/A"}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {u.marital_status || "Unspecified"}
                          </span>
                        </div>
                      </td>

                      {/* Location */}
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-200">{u.city}</span>
                          <span className="text-[10px] text-slate-400">{u.state}</span>
                          {u.stored_state && (
                            <span
                              className="text-[10px] text-amber-400/80"
                              title="State saved on the profile; shown here corrected from the city"
                            >
                              Saved as: {u.stored_state}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Type & Progress */}
                      <td className="py-3.5 px-4 align-top">
                        <div className="flex flex-col gap-1.5 items-start">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight border ${
                              u.user_type === "registered"
                                ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                                : "bg-amber-500/10 text-amber-400 border-amber-500/20"
                            }`}
                          >
                            {u.user_type}
                          </span>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  u.progress === 100 ? "bg-emerald-500" : "bg-blue-500"
                                }`}
                                style={{ width: `${Math.min(100, u.progress || 0)}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-mono text-slate-400">
                              {u.progress}%
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Action */}
                      <td className="py-3.5 px-4 align-top text-right">
                        <Link
                          to={`/users/user/${encodeURIComponent(u.user_id)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-sky-600/20 to-indigo-600/20 hover:from-sky-600 hover:to-indigo-600 text-sky-300 hover:text-white border border-sky-500/30 text-xs font-semibold transition shadow-sm"
                        >
                          <span>Profile</span>
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <div>
              Showing {users.length > 0 ? userPage * USER_PAGE_SIZE + 1 : 0} to{" "}
              {Math.min((userPage + 1) * USER_PAGE_SIZE, totalUsersCount)} of {totalUsersCount} users
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={userPage === 0 || loadingUsers}
                onClick={() => setUserPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Previous
              </button>
              <span className="font-mono font-bold text-slate-300">
                Page {userPage + 1} of {Math.max(1, Math.ceil(totalUsersCount / USER_PAGE_SIZE))}
              </span>
              <button
                disabled={(userPage + 1) * USER_PAGE_SIZE >= totalUsersCount || loadingUsers}
                onClick={() => setUserPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateCityAnalytics;
